import React, { forwardRef, ReactElement, Ref } from 'react';
import { Link, LinkProps } from 'react-router';
import classNames from 'classnames';
import Spinner from '../../foundations/Spinner';

export const variantOptions = [
  'primary',
  'secondary',
  'tertiary',
  'text-link',
  'destructive',
] as const;

export type ButtonVariants = (typeof variantOptions)[number];

// Base props for the Button component
interface BaseProps {
  /**
   * Defines the variant of the button. ex: primary, secondary, tertiary, text-link, destructive
   */
  variant?: ButtonVariants;
  icon?: ReactElement | JSX.Element;
  /**
   * Defines if the button is a circle -- only used for buttons which is just an icon.
   */
  circle?: boolean;
  /**
   * Defines if the button should take up the full width of its container.
   */
  full?: boolean;
  /**
   * Defines if the button is in a loading state.
   */
  loading?: boolean;
  /**
   * Defines if the button is disabled.
   */
  disabled?: boolean;
  /**
   * Defines the behaviour of the button on mobile
   */
  expandOnMobile?: boolean;
  /**
   * Defines the alignment of the text in the button
   */
  align?: 'left' | 'center';
  /**
   * Defines the alignment of the icon in the button
   */
  iconAlignment?: 'left' | 'right';
}

type PolymorphicProps<E extends React.ElementType> = React.PropsWithChildren<
  React.ComponentPropsWithoutRef<E> & {
    /**
     * Defines the tag to be used for the button. ex: 'a', 'button', Link
     */
    as?: E;
  }
>;

export type ElementProps<T extends React.ElementType> = PolymorphicProps<T> &
  BaseProps;

const variantClasses = {
  primary: classNames(
    'border-2 border-transparent bg-primary-special px-s py-xxs text-inverse',
    [
      'hover:bg-primary-interactive',
      'disabled:bg-foreground-regular',
      'focus:border-foreground-strong',
    ]
  ),
  secondary: classNames(
    'border-2 border-primary-special px-s py-xxs text-primary-special bg-regular',
    [
      'hover:bg-special',
      'focus:border-foreground-strong focus:bg-special',
      'disabled:border-foreground-regular disabled:bg-regular disabled:text-subtle',
    ]
  ),
  tertiary: classNames(
    'border-2 border-transparent px-s py-xxs text-primary-special',
    [
      'underline-offset-4 hover:underline',
      'disabled:no-underline disabled:text-subtle',
      'focus:border-foreground-strong focus:underline',
    ]
  ),
  'text-link': classNames(
    'border-2 border-transparent px-0 text-primary-special',
    [
      'underline-offset-4 hover:underline',
      'disabled:no-underline disabled:text-subtle',
      'focus:border-foreground-strong focus:underline',
    ]
  ),
  destructive: classNames(
    'border-2 border-transparent px-s py-xxs text-error-foreground underline-offset-4',
    [
      'hover:underline',
      'disabled:no-underline disabled:text-subtle',
      'focus:border-foreground-strong focus:underline',
    ]
  ),
};

const Content = ({ icon, iconAlignment, children }) => {
  if (icon) {
    return (
      <span className="flex items-center justify-center gap-xxs font-inherit text-inherit">
        {iconAlignment === 'left' && <span>{icon}</span>}
        {Boolean(children) && (
          <span className="font-inherit text-inherit">{children}</span>
        )}
        {iconAlignment === 'right' && <span>{icon}</span>}
      </span>
    );
  }

  return <>{children}</>;
};

type ButtonElement = (<C extends React.ElementType = 'button'>(
  props: ElementProps<C>
) => JSX.Element) & { displayName?: string };

const Button = forwardRef(
  <T extends React.ElementType>(
    {
      as,
      children,
      circle,
      disabled,
      expandOnMobile = true,
      full,
      icon,
      loading = false,
      variant = 'primary',
      align = 'center',
      className,
      iconAlignment = 'left',
      ...props
    }: ElementProps<T>,
    ref: Ref<HTMLButtonElement>
  ) => {
    const Component = as || 'button';
    return (
      <Component
        ref={ref}
        aria-busy={loading}
        disabled={disabled || loading}
        className={classNames(
          {
            'w-full': full,
            'rounded-[50%] h-[44px] w-[44px] flex justify-center items-center':
              circle,
            'rounded-xxxs w-fit': !circle,
            'mobile:w-full': !circle && expandOnMobile,
            'text-left': align === 'left',
            'text-center': align === 'center',
          },
          [variantClasses[variant]],
          'inline-block cursor-pointer select-none font-body text-body font-bold disabled:cursor-not-allowed',
          className
        )}
        {...props}
      >
        {loading ? (
          // we need this to center the spinner
          <span>
            <Spinner full size="S" color="white" />
            <span className="invisible block h-0">
              <Content icon={icon} iconAlignment={iconAlignment}>
                {children}
              </Content>
            </span>
          </span>
        ) : (
          <Content icon={icon} iconAlignment={iconAlignment}>
            {children}
          </Content>
        )}
      </Component>
    );
  }
) as ButtonElement;

Button.displayName = 'Button';

/**
 * This is a custom component because react-router v3 does not have a proper type for Link.
 * This component should be removed and we should use the Button component directly when we upgrade react-router.
 */
export const LinkButton = forwardRef(
  (
    { children, ...props }: LinkProps & BaseProps,
    ref: Ref<HTMLAnchorElement>
  ) => (
    // @ts-expect-error - Link in react-router v3 is not properly typed
    <Button as={Link} ref={ref} {...props}>
      {children}
    </Button>
  )
);

LinkButton.displayName = 'LinkButton';

export default Button;
